import React, { useMemo } from 'react';
import { M2mSnapshot } from '../../../services/m2mService';
import { GENERAL_ID } from '../../../components/Stores/exposureDataStore';
import { buildFxRates, computeActualWorth, DetailRow } from './M2mManagement';

interface Props {
  snapshot: M2mSnapshot | null;
  formulaId: string;
}

const fmt = (n: number) => {
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};
const fmtDec = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });


export const M2mDetail: React.FC<Props> = ({ snapshot, formulaId }) => {
  const isGeneral = formulaId === GENERAL_ID || !formulaId;

  if (!snapshot) {
    return (
      <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4">
        <div className="text-gray-400 text-sm text-center py-8">No M2M data</div>
      </div>
    );
  }

  if (isGeneral) return <SegmentsPanel snapshot={snapshot} />;
  return <MaterialSensitivityPanel snapshot={snapshot} formulaId={formulaId} />;
};

// ─── General: Financial Segments (per-currency breakdown) ───

const SegmentsPanel: React.FC<{ snapshot: M2mSnapshot }> = ({ snapshot }) => {
  const segments = snapshot.financialSegments;

  return (
    <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4 max-h-full overflow-y-auto">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Currency Segments</h3>

      {segments.length === 0 ? (
        <div className="text-gray-400 text-sm text-center py-6">No segment data</div>
      ) : (
        <div className="space-y-4">
          {segments.map((seg, i) => (
            <div key={i} className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-gray-700">{seg.segmentLabel}</span>
                <span className="text-[10px] text-gray-400">{seg.currency}</span>
              </div>
              <div className="space-y-1 text-xs">
                <Row label="Ending Balance" value={fmt(seg.endingBalance)} />
                <Row label="Open Trade Equity" value={fmt(seg.futOpenTradeEquity)} color={seg.futOpenTradeEquity < 0 ? 'text-red-500' : 'text-green-600'} />
                <Row label="Total Equity" value={fmt(seg.totalEquity)} />
                <Row label="Acct Value at Market" value={fmt(seg.acctValueAtMarket)} bold />
                <div className="border-t border-gray-50 my-1" />
                <Row label="Initial Margin" value={fmt(seg.initialMarginRequirement)} color="text-orange-500" />
                <Row label="Maintenance Margin" value={fmt(seg.maintenanceMarginRequirement)} />
                <Row label="Margin Excess" value={fmt(seg.marginExcess)} color={seg.marginExcess < 0 ? 'text-red-500' : 'text-green-600'} bold />
                {seg.conversionRateToUsd !== 1 && (
                  <Row label="FX → USD" value={fmtDec(seg.conversionRateToUsd)} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[10px] text-gray-400 pt-3 mt-auto border-t border-gray-100">
        Statement: {snapshot.statementDate}
      </div>
    </div>
  );
};

// ─── Material: Position breakdown per contract month ───

const MaterialSensitivityPanel: React.FC<{ snapshot: M2mSnapshot; formulaId: string }> = ({ snapshot, formulaId }) => {
  const fxRates = useMemo(() => buildFxRates(snapshot), [snapshot]);

  const groups = useMemo(
    () => snapshot.groups
      .filter(g => g.formulaCode === formulaId)
      .sort((a, b) => a.contractMonth.localeCompare(b.contractMonth)),
    [snapshot.groups, formulaId],
  );

  const commodity = groups[0]?.commodity || formulaId;
  const currency = groups[0]?.currency || 'USD';
  const fxRate = fxRates[currency] || 1;
  const needsFx = currency !== 'USD' && fxRate !== 1;

  const totalLots = groups.reduce((s, g) => s + g.lots, 0);
  const totalPnl = groups.reduce((s, g) => s + g.openPnl * (fxRates[g.currency] || 1), 0);
  const positionValue = computeActualWorth(groups, fxRates);
  const lotSize = groups[0]?.lotSize || 0;
  const totalMT = totalLots * lotSize;

  // Weighted avg entry & close prices
  const avgEntry = totalLots > 0
    ? groups.reduce((s, g) => s + g.avgTradePrice * g.lots, 0) / totalLots : 0;
  const avgClose = totalLots > 0
    ? groups.reduce((s, g) => s + g.closePrice * g.lots, 0) / totalLots : 0;

  const fmtPrice = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4 max-h-full overflow-y-auto">
      {/* Header — matches general panel style */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-base font-semibold text-gray-900">{commodity}</span>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${
          totalPnl >= 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
        }`}>
          M2M {fmt(totalPnl)}
        </span>
      </div>

      {groups.length === 0 ? (
        <div className="text-gray-400 text-sm text-center py-6">No positions for this commodity</div>
      ) : (
        <>
          {/* Waterfall breakdown — same DetailRow style as general panel */}
          <div className="divide-y divide-gray-100">
            <DetailRow color="#6366F1" label="Position Value" value={fmt(positionValue)} bold />
            <div className="text-[10px] text-gray-400 pl-5 py-0.5">
              = {totalLots} lots × {lotSize} MT × {fmtPrice(avgClose)} {currency}{needsFx ? ` × ${fmtDec(fxRate)}` : ''}
            </div>
            <div className="border-t border-gray-100" />
            <DetailRow color="#94A3B8" label="Total Lots" value={`${totalLots}`} />
            <DetailRow color="#94A3B8" label="Quantity" value={`${totalMT.toLocaleString()} MT`} />
            <DetailRow color="#94A3B8" label="Lot Size" value={`${lotSize} MT`} />
            <div className="border-t border-gray-200" />
            <DetailRow color="#F59E0B" label="Avg Entry Price" value={`${fmtPrice(avgEntry)} ${currency}`} />
            <DetailRow color="#0EA5E9" label="Close / Settlement" value={`${fmtPrice(avgClose)} ${currency}`} />
            <div className="border-t border-gray-200" />
            <DetailRow
              color={totalPnl >= 0 ? '#22c55e' : '#EF4444'}
              label="Open P&L"
              value={fmt(totalPnl)}
              bold
            />
            <div className="text-[10px] text-gray-400 pl-5 py-0.5">= (Close − Entry) × Lots × Lot Size</div>
            {needsFx && (
              <DetailRow color="#8B5CF6" label={`FX ${currency} → USD`} value={fmtDec(fxRate)} />
            )}
          </div>

          {/* Per-month breakdown — matches currency segments style */}
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Per Contract Month</div>
            <div className="space-y-2">
              {groups.map((g, i) => {
                const rate = fxRates[g.currency] || 1;
                const pnlUsd = g.openPnl * rate;
                const worth = Math.abs(g.lots) * (g.lotSize || 0) * g.closePrice * rate;
                return (
                  <div key={i} className="bg-gray-50 rounded p-2">
                    <div className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                      <span>{g.contractMonth}</span>
                      <span className={`tabular-nums font-bold ${pnlUsd < 0 ? 'text-red-500' : 'text-green-600'}`}>{fmt(pnlUsd)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-gray-500">
                      <span>Lots: <span className="text-gray-700 tabular-nums">{g.lots} ({g.positionSide})</span></span>
                      <span>Entry: <span className="text-gray-700 tabular-nums">{fmtPrice(g.avgTradePrice)}</span></span>
                      <span>Close: <span className="text-gray-700 tabular-nums">{fmtPrice(g.closePrice)}</span></span>
                      <span>Value: <span className="text-blue-500 tabular-nums">{fmt(worth)}</span></span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <div className="text-[10px] text-gray-400 pt-2 mt-auto border-t border-gray-100">
        Statement: {snapshot.statementDate}
      </div>
    </div>
  );
};

// ─── Shared row helper ───

const Row: React.FC<{ label: string; value: string; color?: string; bold?: boolean }> = ({ label, value, color, bold }) => (
  <div className="flex justify-between">
    <span className="text-gray-500">{label}</span>
    <span className={`tabular-nums ${bold ? 'font-semibold' : ''} ${color || 'text-gray-700'}`}>{value}</span>
  </div>
);
